/**
 * OfferingDetail.jsx — Phase 2B Offering Management
 *
 * Admin page for a single assessment offering:
 *   Sessions (create, list, status transitions, invigilation)
 *   Eligibility & Registration (derive eligibility → register student)
 *
 * Error handling: 403, 404, 409, 422 responses surfaced with meaningful messages.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, PlusCircle, RefreshCw, Users, CalendarDays,
  ShieldCheck, CheckCircle, AlertTriangle, UserPlus, ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import { AssessmentDeliveryShell } from "../components/assessment-delivery/AssessmentDeliveryShell";
import { DeliveryStatusBadge } from "../components/assessment-delivery/DeliveryStatusBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}

async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch {
      msg = r.statusText;
    }
    throw Object.assign(new Error(msg || r.statusText), { status: r.status });
  }
  return r.json();
}

const SESSION_TYPES = ["MAIN", "RESIT", "MOCK", "PRACTICE"];
const SESSION_STATUS_TRANSITIONS = {
  SCHEDULED: ["OPEN", "CANCELLED"],
  OPEN:      ["CLOSED", "SUSPENDED"],
  SUSPENDED: ["OPEN", "CLOSED"],
  CLOSED:    ["ARCHIVED"],
  ARCHIVED:  [],
  CANCELLED: [],
};

export default function OfferingDetail() {
  const { id: offeringId } = useParams();
  const navigate = useNavigate();

  const [offering, setOffering]           = useState(null);
  const [sessions, setSessions]           = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading]             = useState(true);

  // Session create form
  const [showSession, setShowSession]     = useState(false);
  const [sessionForm, setSessionForm]     = useState({
    title: "", session_type: "MAIN", scheduled_date: "",
    start_time: "", end_time: "", duration_minutes: "60", notes: "",
  });
  const [savingSession, setSavingSession] = useState(false);

  // Register student form
  const [showRegister, setShowRegister]   = useState(false);
  const [regStudentId, setRegStudentId]   = useState("");
  const [savingReg, setSavingReg]         = useState(false);

  // Invigilation form (per session)
  const [invigilating, setInvigilating]   = useState(null); // session_id or null
  const [invig, setInvig]                 = useState({ staff_user_id: "", role_in_session: "ASSISTANT" });
  const [savingInvig, setSavingInvig]     = useState(false);

  // Session transition loading
  const [transitionLoading, setTransitionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [off, sess, regs] = await Promise.all([
        apiFetch(`/delivery/offerings/${offeringId}`),
        apiFetch(`/delivery/offerings/${offeringId}/sessions`),
        apiFetch(`/delivery/registrations?offering_id=${offeringId}`),
      ]);
      setOffering(off);
      setSessions(sess);
      setRegistrations(regs);
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [offeringId]);

  useEffect(() => { load(); }, [load]);

  // ── Create session ───────────────────────────────────────────────────────
  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!sessionForm.title.trim()) { toast.error("Session title required"); return; }
    if (!sessionForm.duration_minutes) { toast.error("Duration required"); return; }
    setSavingSession(true);
    try {
      const payload = {
        title: sessionForm.title.trim(),
        session_type: sessionForm.session_type,
        duration_minutes: Number(sessionForm.duration_minutes),
        scheduled_date: sessionForm.scheduled_date || null,
        start_time: sessionForm.start_time || null,
        end_time: sessionForm.end_time || null,
        notes: sessionForm.notes.trim() || null,
      };
      await apiFetch(`/delivery/offerings/${offeringId}/sessions`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      toast.success("Session created");
      setShowSession(false);
      setSessionForm({
        title: "", session_type: "MAIN", scheduled_date: "",
        start_time: "", end_time: "", duration_minutes: "60", notes: "",
      });
      load();
    } catch (e) {
      toast.error(`Create session failed (${e.status || ""}): ${e.message}`);
    } finally {
      setSavingSession(false);
    }
  };

  // ── Session status transition ────────────────────────────────────────────
  const handleSessionTransition = async (sessionId, newStatus) => {
    setTransitionLoading(sessionId + newStatus);
    try {
      await apiFetch(`/delivery/sessions/${sessionId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Session → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(`Transition failed (${e.status || ""}): ${e.message}`);
    } finally {
      setTransitionLoading(null);
    }
  };

  // ── Register student ─────────────────────────────────────────────────────
  const handleRegisterStudent = async (e) => {
    e.preventDefault();
    if (!regStudentId.trim()) { toast.error("Student User ID required"); return; }
    setSavingReg(true);
    try {
      // Step 1: derive eligibility
      const elig = await apiFetch("/delivery/eligibility-decisions", {
        method: "POST",
        body: JSON.stringify({
          offering_id: offeringId,
          student_id: regStudentId.trim(),
        }),
      });
      // Step 2: register
      await apiFetch("/delivery/registrations", {
        method: "POST",
        body: JSON.stringify({
          offering_id: offeringId,
          student_id: regStudentId.trim(),
          eligibility_decision_id: elig.id,
        }),
      });
      toast.success("Student registered successfully");
      setRegStudentId("");
      setShowRegister(false);
      load();
    } catch (e) {
      if (e.status === 409) {
        toast.warning(`Registration conflict: ${e.message}`);
      } else if (e.status === 422) {
        toast.error(`Validation error: ${e.message}`);
      } else {
        toast.error(`Register failed (${e.status || ""}): ${e.message}`);
      }
    } finally {
      setSavingReg(false);
    }
  };

  // ── Assign invigilator ───────────────────────────────────────────────────
  const handleAssignInvigilator = async (e, sessionId) => {
    e.preventDefault();
    if (!invig.staff_user_id.trim()) { toast.error("Staff User ID required"); return; }
    setSavingInvig(true);
    try {
      await apiFetch(`/delivery/sessions/${sessionId}/invigilation-assignments`, {
        method: "POST",
        body: JSON.stringify({
          staff_user_id: invig.staff_user_id.trim(),
          role_in_session: invig.role_in_session,
        }),
      });
      toast.success("Invigilator assigned");
      setInvigilating(null);
      setInvig({ staff_user_id: "", role_in_session: "ASSISTANT" });
    } catch (e) {
      toast.error(`Assign failed (${e.status || ""}): ${e.message}`);
    } finally {
      setSavingInvig(false);
    }
  };

  if (loading) {
    return (
      <AssessmentDeliveryShell title="Loading…" breadcrumb="Offering Detail">
        <div className="text-sm text-slate-500 py-12 text-center" data-testid="offering-detail-loading">
          Loading offering…
        </div>
      </AssessmentDeliveryShell>
    );
  }

  if (!offering) {
    return (
      <AssessmentDeliveryShell title="Not Found" breadcrumb="Offering Detail">
        <div className="text-sm text-red-400 py-12 text-center" data-testid="offering-detail-not-found">
          Offering not found or access denied.
        </div>
      </AssessmentDeliveryShell>
    );
  }

  return (
    <AssessmentDeliveryShell
      title={offering.title || offering.code || "Offering Detail"}
      breadcrumb="Offering Detail"
    >
      {/* ── Back + header ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 mb-8">
        <button
          type="button"
          onClick={() => navigate("/assessment-delivery")}
          data-testid="back-to-offerings"
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All Offerings
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="offering-detail-refresh"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400
                     border border-slate-700 bg-slate-900/60 hover:bg-slate-800 transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* ── Offering info card ───────────────────────────────────────────── */}
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-6 py-5 mb-8"
           data-testid="offering-info-card">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <DeliveryStatusBadge status={offering.status} />
              <span className="text-xs text-slate-500 font-mono">{offering.code}</span>
              <span className="text-xs text-slate-500">{offering.offering_type}</span>
            </div>
            <h2 className="text-lg font-semibold text-slate-100">{offering.title || offering.code}</h2>
            {offering.description && (
              <p className="text-sm text-slate-400 mt-1">{offering.description}</p>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Max {offering.max_attempts} attempt{offering.max_attempts !== 1 ? "s" : ""} ·
              {" "}timeout: {(offering.timeout_action || "AUTO_SUBMIT").replace(/_/g, " ")}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sessions ─────────────────────────────────────────────────────── */}
      <section className="mb-10" data-testid="sessions-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-emerald-400" />
            Sessions ({sessions.length})
          </h3>
          <button
            type="button"
            onClick={() => setShowSession(v => !v)}
            data-testid="add-session-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       text-emerald-300 border border-emerald-700/60 bg-emerald-950/30
                       hover:bg-emerald-900/40 transition-colors"
          >
            <PlusCircle className="w-3.5 h-3.5" /> Add Session
          </button>
        </div>

        {/* Session create form */}
        {showSession && (
          <div className="mb-5 rounded-xl border border-emerald-800/40 bg-slate-900/60 p-6"
               data-testid="create-session-form">
            <h4 className="text-sm font-semibold text-slate-200 mb-4">New Session</h4>
            <form onSubmit={handleCreateSession} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs text-slate-400 mb-1">Title *</label>
                <input
                  type="text"
                  value={sessionForm.title}
                  onChange={e => setSessionForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. CS101 Final — Morning Slot"
                  required
                  data-testid="session-form-title"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Type</label>
                <select
                  value={sessionForm.session_type}
                  onChange={e => setSessionForm(f => ({ ...f, session_type: e.target.value }))}
                  data-testid="session-form-type"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-emerald-600"
                >
                  {SESSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Duration (minutes) *</label>
                <input
                  type="number"
                  min="1"
                  value={sessionForm.duration_minutes}
                  onChange={e => setSessionForm(f => ({ ...f, duration_minutes: e.target.value }))}
                  required
                  data-testid="session-form-duration"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-emerald-600"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Scheduled Date</label>
                <input
                  type="date"
                  value={sessionForm.scheduled_date}
                  onChange={e => setSessionForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  data-testid="session-form-date"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-emerald-600"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Notes</label>
                <input
                  type="text"
                  value={sessionForm.notes}
                  onChange={e => setSessionForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes"
                  data-testid="session-form-notes"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
                />
              </div>
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={savingSession}
                  data-testid="create-session-submit"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-600
                             text-white transition-colors disabled:opacity-50"
                >
                  {savingSession ? "Creating…" : "Create Session"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSession(false)}
                  data-testid="cancel-session-btn"
                  className="px-4 py-2 rounded-lg text-sm text-slate-400 border border-slate-700
                             hover:border-slate-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Sessions list */}
        {sessions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center"
               data-testid="no-sessions">
            <p className="text-slate-500 text-sm">No sessions yet. Add a session to allow attempts.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="sessions-list">
            {sessions.map(s => (
              <SessionCard
                key={s.id}
                session={s}
                onTransition={handleSessionTransition}
                transitionLoading={transitionLoading}
                onAssignInvig={() => setInvigilating(s.id)}
                showInvigForm={invigilating === s.id}
                invigForm={invig}
                onInvigChange={setInvig}
                onInvigSubmit={(e) => handleAssignInvigilator(e, s.id)}
                savingInvig={savingInvig}
                onCancelInvig={() => setInvigilating(null)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Registrations ───────────────────────────────────────────────── */}
      <section data-testid="registrations-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <Users className="w-4 h-4 text-sky-400" />
            Registrations ({registrations.length})
          </h3>
          <button
            type="button"
            onClick={() => setShowRegister(v => !v)}
            data-testid="register-student-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       text-sky-300 border border-sky-700/60 bg-sky-950/30
                       hover:bg-sky-900/40 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" /> Register Student
          </button>
        </div>

        {/* Register form */}
        {showRegister && (
          <div className="mb-5 rounded-xl border border-sky-800/40 bg-slate-900/60 p-6"
               data-testid="register-student-form">
            <h4 className="text-sm font-semibold text-slate-200 mb-1">Register Student</h4>
            <p className="text-xs text-slate-500 mb-4">
              Derives eligibility and registers the student in one step.
            </p>
            <form onSubmit={handleRegisterStudent} className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs text-slate-400 mb-1">Student User ID *</label>
                <input
                  type="text"
                  value={regStudentId}
                  onChange={e => setRegStudentId(e.target.value)}
                  placeholder="MongoDB user_id of the student"
                  required
                  data-testid="register-student-id-input"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                             text-slate-200 focus:outline-none focus:border-sky-600 placeholder-slate-600"
                />
              </div>
              <button
                type="submit"
                disabled={savingReg}
                data-testid="register-student-submit"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-700 hover:bg-sky-600
                           text-white transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {savingReg ? "Registering…" : "Register"}
              </button>
              <button
                type="button"
                onClick={() => setShowRegister(false)}
                data-testid="cancel-register-btn"
                className="px-4 py-2 rounded-lg text-sm text-slate-400 border border-slate-700
                           hover:border-slate-600 transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Registrations list */}
        {registrations.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center"
               data-testid="no-registrations">
            <p className="text-slate-500 text-sm">No students registered for this offering yet.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="registrations-list">
            {registrations.map(reg => (
              <div
                key={reg.id}
                className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-3"
                data-testid={`reg-row-${reg.id}`}
              >
                <DeliveryStatusBadge status={reg.status} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-slate-300 truncate">{reg.student_id}</p>
                  <p className="text-xs text-slate-500">Reg ID: {reg.id?.slice(0, 12)}…</p>
                </div>
                <span className="text-xs text-slate-500">
                  {reg.created_at?.slice(0, 10)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </AssessmentDeliveryShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SessionCard({
  session: s,
  onTransition,
  transitionLoading,
  onAssignInvig,
  showInvigForm,
  invigForm,
  onInvigChange,
  onInvigSubmit,
  savingInvig,
  onCancelInvig,
}) {
  const nextStatuses = SESSION_STATUS_TRANSITIONS[s.status] ?? [];
  const isLoading = (key) => transitionLoading === (s.id + key);

  return (
    <div
      className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden"
      data-testid={`session-card-${s.id}`}
    >
      <div className="flex items-center gap-4 px-5 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <DeliveryStatusBadge status={s.status} />
            <span className="text-xs text-slate-500">{s.session_type}</span>
            {s.scheduled_date && (
              <span className="text-xs text-slate-600 flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {s.scheduled_date}
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-slate-200">{s.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {s.duration_minutes} min
            {s.notes && ` · ${s.notes}`}
          </p>
        </div>

        {/* Status transitions */}
        <div className="flex items-center gap-1.5">
          {nextStatuses.map(ns => (
            <button
              key={ns}
              type="button"
              disabled={isLoading(ns)}
              onClick={() => onTransition(s.id, ns)}
              data-testid={`session-transition-${s.id}-${ns}`}
              className="px-2.5 py-1 rounded-lg text-xs border border-slate-700 text-slate-300
                         bg-slate-800 hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {isLoading(ns) ? "…" : `→ ${ns}`}
            </button>
          ))}
          {/* Assign invigilator */}
          {(s.status === "SCHEDULED" || s.status === "OPEN") && (
            <button
              type="button"
              onClick={onAssignInvig}
              data-testid={`assign-invig-btn-${s.id}`}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs border
                         border-indigo-700/60 text-indigo-300 bg-indigo-950/30
                         hover:bg-indigo-900/40 transition-colors"
            >
              <ShieldCheck className="w-3 h-3" /> Invigilator
            </button>
          )}
        </div>
      </div>

      {/* Invigilation assignment form */}
      {showInvigForm && (
        <div className="border-t border-slate-800 bg-slate-900/30 px-5 py-4"
             data-testid={`invig-form-${s.id}`}>
          <form onSubmit={onInvigSubmit} className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">Staff User ID *</label>
              <input
                type="text"
                value={invigForm.staff_user_id}
                onChange={e => onInvigChange(f => ({ ...f, staff_user_id: e.target.value }))}
                placeholder="User ID of the invigilator"
                required
                data-testid={`invig-staff-id-${s.id}`}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-indigo-600 placeholder-slate-600"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select
                value={invigForm.role_in_session}
                onChange={e => onInvigChange(f => ({ ...f, role_in_session: e.target.value }))}
                data-testid={`invig-role-${s.id}`}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-indigo-600"
              >
                <option value="CHIEF">CHIEF</option>
                <option value="ASSISTANT">ASSISTANT</option>
                <option value="OBSERVER">OBSERVER</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={savingInvig}
              data-testid={`invig-submit-${s.id}`}
              className="px-4 py-2 rounded-lg text-sm bg-indigo-700 hover:bg-indigo-600
                         text-white transition-colors disabled:opacity-50"
            >
              {savingInvig ? "Assigning…" : "Assign"}
            </button>
            <button
              type="button"
              onClick={onCancelInvig}
              data-testid={`invig-cancel-${s.id}`}
              className="px-4 py-2 rounded-lg text-sm text-slate-400 border border-slate-700
                         hover:border-slate-600 transition-colors"
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
